class TechTeesWeek extends HTMLElement {
  static attempt = null;
  static history = [];
  static resolve(product, colour, size) {
    const variant = product?.variants?.find(v => v.available === true && v.options?.[product.ci] === colour && v.options?.[product.si] === size);
    return { colour, size: variant ? size : null, variant };
  }

  static validProducts(products, complete) {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    return complete === true && products.length === 5 && new Set(products.map(p => String(p?.id))).size === 5 && products.every((p, i) => p?.published === true && p.day === days[i] && /^\d+$/.test(String(p.id)) && p.ci >= 0 && p.si >= 0 && p.ci !== p.si && p.variants?.some(v => v.available === true));
  }

  static items(products, choices, complete) {
    if (!this.validProducts(products, complete) || choices.length !== 5) return null;
    const variants = products.map((p, i) => this.resolve(p, choices[i]?.colour, choices[i]?.size).variant);
    if (variants.some(v => !v || !/^\d+$/.test(String(v.id))) || new Set(variants.map(v => String(v.id))).size !== 5) return null;
    return variants.map(v => ({ id: String(v.id), quantity: 1 }));
  }

  static commonSizes(products, choices, complete) {
    if (!this.validProducts(products, complete)) return [];
    const sizes = products.map((p, i) => [...new Set(p.variants.filter(v => v.available === true && v.options[p.ci] === choices[i]?.colour).map(v => v.options[p.si]))]);
    return sizes[0].filter(size => size && sizes.every(values => values.includes(size)));
  }

  connectedCallback() {
    if (this.events) return;
    const retainedChoices = this.products?.map((p, i) => ({ productId: p?.id, colour: this.choices[i]?.colour, size: this.choices[i]?.size }));
    this.events = new AbortController();
    this.rows = [...this.querySelectorAll('[data-tt-week-day]')];
    this.products = this.rows.map(row => {
      try {
        const p = JSON.parse(row.querySelector('[data-tt-week-product]')?.textContent || 'null');
        return p && Array.isArray(p.variants) && p.variants.every(v => Array.isArray(v.options)) ? p : null;
      } catch { return null; }
    });
    this.choices = this.products.map(p => TechTeesWeek.resolve(p, p?.defaultColour, null));
    this.complete = this.dataset.complete === 'true';
    this.status = this.querySelector('[data-tt-week-status]');
    this.looks = [...this.querySelectorAll('.tt-week__look')];
    this.cards = [...this.querySelectorAll('.tt-week__design')];
    this.lines = [...this.querySelectorAll('.tt-week__rw')];
    this.tabs = [...this.querySelectorAll('[data-tt-week-tabs] [data-tt-day]')];
    // The photos and design cards duplicate the day tabs (same target, same result): leave them clickable but out of the Tab order.
    [...this.looks, ...this.cards].forEach(link => link.setAttribute('tabindex', '-1'));
    // The card strip scrolls sideways on phones, so the strip itself is the one keyboard stop that can scroll it.
    this.querySelector('.tt-week__cards')?.setAttribute('tabindex', '0');
    if (!this.rows.length || this.tabs.length !== this.rows.length) return;
    this.querySelector('[data-tt-week-tabs]').setAttribute('role', 'tablist');
    this.rows.forEach((row, i) => {
      const panel = row.querySelector('[data-tt-week-panel]');
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', this.tabs[i].id);
      this.tabs[i].setAttribute('role', 'tab');
      this.tabs[i].setAttribute('aria-controls', panel.id);
      const controls = row.querySelector('[data-tt-controls]');
      if (controls && this.products[i]) controls.hidden = false;
    });
    const options = { signal: this.events.signal };
    this.addEventListener('click', event => this.click(event), options);
    this.addEventListener('change', event => this.change(event), options);
    this.addEventListener('keydown', event => this.keydown(event), options);
    this.addEventListener('submit', event => this.submit(event), options);
    this.swipe(options);
    this.classList.add('tt-week--enhanced');
    this.active = 0;
    this.restoreAttempt();
    this.restoreChoices(retainedChoices || this.attempt?.choices);
    this.renderHistory();
    this.selectDay(0, false);
    this.rows.forEach((_, i) => this.sync(i));
    this.updateSet();
    if (this.attempt) this.announce('A previous Week addition needs review. Check cart status before adding more.');
  }

  disconnectedCallback() {
    this.events?.abort();
    this.request?.abort();
    this.events = null;
    this.request = null;
    this.pending = false;
  }

  swipe(options) {
    const lineup = this.querySelector('.tt-week__lineup');
    let from = null;
    lineup?.addEventListener('touchstart', event => { const t = event.touches?.[0]; from = t && !this.pending ? { x: t.clientX, y: t.clientY } : null; }, { ...options, passive: true });
    lineup?.addEventListener('touchend', event => {
      const t = event.changedTouches?.[0], start = from;
      from = null;
      if (!t || !start || !window.matchMedia?.('(max-width: 899.98px)').matches) return;
      const dx = t.clientX - start.x;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(t.clientY - start.y)) this.selectDay((this.active + (dx < 0 ? 1 : -1) + this.rows.length) % this.rows.length);
    }, options);
  }

  announce(message) { this.status.textContent = message; }

  selectDay(index, focus = false) {
    if (index < 0 || index >= this.rows.length) return;
    const previous = this.choices[this.active];
    const next = this.choices[index];
    if (index !== this.active && !next.size && previous?.size) {
      this.choices[index] = TechTeesWeek.resolve(this.products[index], next.colour, previous.size);
      this.sync(index);
    }
    this.active = index;
    this.rows.forEach((row, i) => {
      row.classList.toggle('tt-week__day--selected', i === index);
      row.querySelector('[data-tt-week-panel]').hidden = i !== index;
      this.tabs[i].setAttribute('aria-selected', String(i === index));
      this.tabs[i].tabIndex = i === index ? 0 : -1;
    });
    this.looks?.forEach((look, i) => look.classList.toggle('tt-week__look--selected', i === index));
    this.cards?.forEach((card, i) => { card.classList.toggle('tt-week__design--selected', i === index); if (i === index) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current'); });
    if (focus) this.tabs[index].focus();
    this.updateSet();
  }

  click(event) {
    const step = event.target.closest('[data-tt-step]');
    if (step && this.contains(step)) this.selectDay((this.active + Number(step.dataset.ttStep) + this.rows.length) % this.rows.length);
    const swatch = event.target.closest('[data-tt-colour-swatch]');
    if (swatch && this.contains(swatch) && !this.pending) {
      const select = swatch.closest('form').querySelector('[data-tt-colour]');
      select.value = swatch.dataset.ttColourSwatch;
      this.change({ target: select });
    }
    const day = event.target.closest('[data-tt-day]');
    if (day && this.contains(day)) {
      event.preventDefault();
      const index = this.rows.findIndex(row => row.dataset.ttWeekDay === day.dataset.ttDay);
      this.selectDay(index, !day.closest('.tt-week__rw'));
      if (day.closest('.tt-week__rw')) this.rows[index].scrollIntoView({ block: 'start' });
    }
    if (event.target.closest('[data-tt-add-five]')) this.addFive();
    if (event.target.closest('[data-tt-recover]')) this.recoverCart();
    if (event.target.closest('[data-tt-new-purchase]')) this.acknowledge();
  }

  keydown(event) {
    if (!this.tabs.includes(event.target)) return;
    const next = { ArrowRight: (this.active + 1) % this.rows.length, ArrowLeft: (this.active + this.rows.length - 1) % this.rows.length, Home: 0, End: this.rows.length - 1 }[event.key];
    if (next !== undefined) { event.preventDefault(); this.selectDay(next, true); }
  }

  change(event) {
    if (this.pending) return;
    if (event.target.matches('[data-tt-common-size]')) {
      const size = event.target.value;
      if (!TechTeesWeek.commonSizes(this.products, this.choices, this.complete).includes(size)) { this.announce('Choose a size available for all five colours.'); return; }
      this.choices = this.products.map((p, i) => TechTeesWeek.resolve(p, this.choices[i].colour, size));
      this.rows.forEach((_, i) => this.sync(i));
      this.announce(`${size} selected for all five tees.`);
    } else {
      const index = this.rows.indexOf(event.target.closest('[data-tt-week-day]'));
      if (index < 0 || !this.products[index]) return;
      const old = this.choices[index];
      if (event.target.matches('[data-tt-colour]')) this.choices[index] = TechTeesWeek.resolve(this.products[index], event.target.value, old.size);
      else if (event.target.matches('[data-tt-size]')) this.choices[index] = TechTeesWeek.resolve(this.products[index], old.colour, event.target.value);
      else return;
      this.sync(index);
      const choice = this.choices[index];
      this.announce(choice.variant ? `${choice.colour}, ${choice.size} selected.` : 'Choose an available size for this colour.');
    }
    this.updateSet();
  }

  sync(index) {
    const row = this.rows[index], p = this.products[index], choice = this.choices[index];
    if (!p) return;
    const form = row.querySelector('form');
    if (!form) return;
    const id = form.querySelector('[name="id"]');
    id.value = choice.variant ? String(choice.variant.id) : '';
    id.disabled = !choice.variant;
    form.querySelector('[name="add"]').disabled = !choice.variant || Boolean(this.pending || this.attempt);
    const colour = form.querySelector('[data-tt-colour]');
    colour.value = choice.colour;
    colour.disabled = Boolean(this.pending);
    colour.hidden = true;
    const colourName = form.querySelector('[data-tt-colour-name]');
    if (colourName) colourName.textContent = choice.colour;
    form.querySelectorAll('[data-tt-colour-swatch]').forEach(button => {
      button.disabled = Boolean(this.pending);
      button.setAttribute('aria-pressed', String(button.dataset.ttColourSwatch === choice.colour));
    });
    form.querySelectorAll('[data-tt-size]').forEach(input => {
      input.disabled = Boolean(this.pending) || !TechTeesWeek.resolve(p, choice.colour, input.value).variant;
      input.checked = choice.size === input.value;
    });
    row.querySelector('[data-tt-price]').textContent = choice.variant?.money || 'Choose a size to see the price.';
    const slug = String(choice.colour).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const card = this.cards?.[index];
    if (card) {
      const name = card.querySelector('[data-tt-card-colour]');
      if (name) name.textContent = choice.colour;
      const dot = card.querySelector('[data-tt-card-dot]');
      if (dot) dot.className = `tt-week__dot tt-week__swatch--${slug}`;
    }
    const line = this.lines?.[index];
    if (line) {
      line.className = `tt-week__rw tt-week__swatch--${slug}`;
      const [what, cost] = line.querySelectorAll('[data-tt-r]');
      what.textContent = `${choice.colour} · ${choice.size || 'Choose a size'}`;
      if (choice.variant) cost.textContent = choice.variant.money;
    }
    const look = this.looks?.[index] || row.querySelector('.tt-week__look');
    if (look.dataset.colour !== choice.colour) {
      look.querySelector('.tt-week__image')?.remove();
      const template = [...row.querySelectorAll('[data-tt-colour-image]')].find(t => t.dataset.ttColourImage === choice.colour);
      if (template) look.prepend(template.content.cloneNode(true));
      look.dataset.colour = choice.colour;
    }
    const detail = row.querySelector('.tt-week__detail-image');
    if (detail && detail.dataset.colour !== choice.colour) {
      const template = [...row.querySelectorAll('[data-tt-colour-image]')].find(t => t.dataset.ttColourImage === choice.colour);
      detail.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      const image = detail.querySelector('img');
      if (image) image.sizes = '(min-width: 900px) 44vw, 100vw';
      detail.dataset.colour = choice.colour;
    }
    const link = row.querySelector('[data-tt-product-link]');
    const url = new URL(p.url, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    if (choice.variant) url.searchParams.set('variant', choice.variant.id);
    link.href = url.pathname + url.search;
  }

  getSelection() {
    const items = TechTeesWeek.items(this.products, this.choices, this.complete);
    return { version: 1, ready: Boolean(items), items: items || [], products: this.products.map((p, i) => ({ productId: p?.id || null, day: p?.day || null, colour: this.choices[i]?.colour || null, size: this.choices[i]?.size || null, variantId: this.choices[i]?.variant?.id || null })) };
  }

  updateSet() {
    const root = this.querySelector('[data-tt-week-set]');
    root.hidden = !TechTeesWeek.validProducts(this.products, this.complete);
    const common = this.querySelector('[data-tt-common-size]');
    const sizes = TechTeesWeek.commonSizes(this.products, this.choices, this.complete);
    common.replaceChildren(new Option('Choose a size', ''));
    sizes.forEach(size => common.add(new Option(size, size)));
    common.disabled = sizes.length === 0 || Boolean(this.pending);
    if (this.choices[0]?.size && this.choices.every(c => c.size === this.choices[0].size)) common.value = this.choices[0].size;
    const selection = this.getSelection();
    this.querySelector('[data-tt-total]').textContent = this.money(this.choices.reduce((sum, c, i) => sum + (c.variant?.price ?? Math.min(...this.products[i].variants.map(v => v.price))), 0));
    const addFive = this.querySelector('[data-tt-add-five]');
    if (addFive) addFive.disabled = !selection.ready || Boolean(this.pending || this.attempt || this.stale);
    const recover = this.querySelector('[data-tt-recover]');
    if (recover) { recover.hidden = !this.attempt; recover.disabled = Boolean(this.pending); }
    const acknowledge = this.querySelector('[data-tt-new-purchase]');
    if (acknowledge) { acknowledge.hidden = !this.attempt?.receipt?.known; acknowledge.disabled = Boolean(this.pending); }
    this.dispatchEvent(new CustomEvent('tt:week-selection', { bubbles: true, detail: selection }));
  }

  money(amount, currency = this.dataset.currency) {
    // Owner style (REQ-050): AUD drops ".00" and uses the A$ prefix; other currencies use Intl.
    if (currency === 'AUD') { const v = amount / 100; return `A$${Number.isInteger(v) ? v : v.toFixed(2)}`; }
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount / 100);
  }

  endpoint(path, suffix = '') {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Unable to verify this request. View your cart.');
    url.pathname += suffix;
    return url;
  }

  async read(url, format = 'json', init = {}, onResponse) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    const signal = this.request.signal;
    if (signal.aborted) abort();
    signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...init, signal: controller.signal });
      if (!onResponse) {
        if (!response.ok) throw new Error('Unable to verify current availability or cart. Please try checking again.');
        return await response[format]();
      }
      let result;
      try { result = await response[format](); }
      catch (error) {
        if (error.name === 'SyntaxError' && !controller.signal.aborted) onResponse(response);
        throw error;
      }
      if (controller.signal.aborted) throw new Error('Request interrupted.');
      onResponse(response);
      if (!response.ok) throw new Error('Cart request was rejected.');
      return result;
    } finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
  }

  lock(busy) {
    this.pending = busy;
    this.setAttribute('aria-busy', String(busy));
    this.rows.forEach((_, i) => this.sync(i));
    this.updateSet();
  }

  restoreAttempt() {
    this.attempt = TechTeesWeek.attempt;
    this.history = TechTeesWeek.history;
    try {
      const saved = JSON.parse(sessionStorage.getItem('tt-week-attempt') || 'null');
      if (!this.attempt && saved && typeof saved.key === 'string' && [1, 5].includes(saved.items?.length) && saved.items.every(i => /^\d+$/.test(i.id) && i.quantity === 1)) this.attempt = saved;
      const history = JSON.parse(sessionStorage.getItem('tt-week-history') || '[]');
      if (Array.isArray(history) && !this.history.length) this.history = history.filter(value => value?.receipt?.known && typeof value.receipt.summary === 'string').slice(-10);
    } catch { /* Memory guards remain available when storage is blocked. */ }
    TechTeesWeek.attempt = this.attempt;
    TechTeesWeek.history = this.history;
  }

  restoreChoices(saved) {
    if (!Array.isArray(saved)) return;
    this.products.forEach((p, i) => {
      const choice = saved.find(c => String(c.productId) === String(p?.id));
      if (choice) this.choices[i] = TechTeesWeek.resolve(p, choice.colour, choice.size);
    });
  }

  saveAttempt(attempt) {
    this.attempt = attempt;
    TechTeesWeek.attempt = attempt;
    try {
      if (attempt) sessionStorage.setItem('tt-week-attempt', JSON.stringify(attempt));
      else sessionStorage.removeItem('tt-week-attempt');
    } catch { /* Never send a request a second time automatically. */ }
  }

  renderHistory() {
    const root = this.querySelector('[data-tt-cart-history]');
    if (!root) return;
    const list = root.querySelector('ol');
    list.replaceChildren();
    (this.history || []).forEach(attempt => {
      const row = document.createElement('li');
      row.textContent = attempt.receipt.firstKnownSummary || attempt.receipt.summary;
      list.append(row);
    });
    root.hidden = !this.history?.length;
  }

  acknowledge() {
    if (this.pending || !this.attempt?.receipt?.known) return;
    this.history = [...(this.history || []), this.attempt].slice(-10);
    TechTeesWeek.history = this.history;
    try { sessionStorage.setItem('tt-week-history', JSON.stringify(this.history)); } catch { /* Keep the in-memory receipts. */ }
    this.saveAttempt(null);
    this.renderHistory();
    this.lock(false);
    this.announce('Cart result acknowledged. Nothing new was added.');
    this.rows[this.active || 0]?.querySelector('[data-tt-colour]')?.focus();
  }

  async freshSelection(selection) {
    const url = new URL(location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    const html = await this.read(url, 'text');
    const freshSection = new DOMParser().parseFromString(html, 'text/html').querySelector('techtees-week');
    const refs = freshSection ? [...freshSection.querySelectorAll('[data-tt-week-product]')].map(node => JSON.parse(node.textContent)) : [];
    if (!freshSection || freshSection.dataset.weekId !== this.dataset.weekId || freshSection.dataset.complete !== 'true' || !TechTeesWeek.validProducts(refs, true) || refs.some((p, i) => String(p.id) !== String(selection.products[i].productId))) {
      this.stale = true;
      throw new Error('The Week lineup changed. Reload this page before adding all five. Your current choices are still shown.');
    }
    const fresh = await Promise.all(this.products.map(p => this.read(this.endpoint(p.url, '.js'))));
    let invalid = false, changed = false;
    fresh.forEach((p, i) => {
      const current = this.products[i], choice = this.choices[i];
      const variant = p.variants?.find(v => String(v.id) === selection.items[i].id);
      if (String(p.id) !== String(current.id) || !variant?.available || !Number.isSafeInteger(variant.price) || variant.price < 0 || variant.options?.length !== choice.variant.options.length || !variant.options.every((value, n) => value === choice.variant.options[n])) {
        this.choices[i] = TechTeesWeek.resolve(current, choice.colour, null);
        invalid = true;
      } else if (variant.price !== choice.variant.price) {
        choice.variant.price = variant.price;
        choice.variant.money = this.money(variant.price);
        changed = true;
      }
    });
    if (invalid) throw new Error('A selected size is no longer available. Choose another size before adding all five.');
    if (changed) throw new Error('Prices changed. Review the updated prices before adding all five.');
  }

  static quantities(cart, key) {
    if (!Array.isArray(cart?.items) || cart.items.some(i => !Number.isInteger(i.quantity) || i.quantity < 0)) throw new Error('Cart response could not be verified.');
    const result = {};
    cart.items.forEach(item => {
      if (key && item.properties?._tt_week_attempt !== key) return;
      const id = String(item.variant_id || item.id);
      result[id] = (result[id] || 0) + item.quantity;
    });
    return result;
  }

  async cartState() {
    const cart = await this.read(this.endpoint(this.dataset.cartUrl, '.js'));
    TechTeesWeek.quantities(cart);
    if (typeof cart.currency !== 'string' || !Number.isSafeInteger(cart.total_price)) throw new Error('Cart totals could not be verified.');
    return cart;
  }

  showCart(cart) {
    const attempt = this.attempt;
    const own = TechTeesWeek.quantities(cart, attempt.key);
    const all = TechTeesWeek.quantities(cart);
    const list = this.querySelector('[data-tt-cart-result]');
    list.replaceChildren(); list.hidden = false;
    let confirmed = 0;
    attempt.items.forEach((item, i) => {
      const quantity = own[item.id] || 0;
      if (quantity === 1) confirmed++;
      const line = document.createElement('li');
      line.textContent = `${attempt.labels[i]}: ${quantity} from this request in cart (${attempt.before[item.id] || 0} before; ${all[item.id] || 0} now).`;
      list.append(line);
    });
    const expected = attempt.items.length;
    const complete = confirmed === expected && Object.keys(own).length === expected;
    const known = Boolean(attempt.receipt?.known || complete || attempt.responseSettled);
    const count = expected === 5 ? 'five' : String(expected);
    const message = complete ? (expected === 5 ? 'All five tees are confirmed in your cart.' : 'Your tee is confirmed in your cart.') : `${confirmed} of ${count} requested tees are confirmed in your cart.`;
    const next = known ? 'Acknowledge the result to start again.' : 'Outcome unknown. A late addition is possible. Check cart status again or view your cart; adding stays locked.';
    const summary = `${message} Cart total: ${this.money(cart.total_price, cart.currency)}. ${attempt.labels.join('; ')}`;
    attempt.receipt = { known, complete, firstKnownSummary: attempt.receipt?.firstKnownSummary || (known ? summary : null), everComplete: Boolean(complete || attempt.receipt?.everComplete), summary };
    this.saveAttempt(attempt);
    this.announce(`${message} Cart total: ${this.money(cart.total_price, cart.currency)}. ${next} View cart for discounts and checkout.`);
    this.dispatchEvent(new CustomEvent('tt:cart-reconciled', { bubbles: true, detail: { cart, complete, items: attempt.items } }));
    return { complete, empty: Object.keys(own).length === 0 };
  }

  async purchase(items, indexes, action) {
    const signal = this.request.signal;
    const before = await this.cartState();
    if (before.currency !== this.dataset.currency) throw new Error('Your currency changed. Reload this page before adding.');
    const endpoint = this.endpoint(action, '.js');
    if (!/\/cart\/add\.js$/.test(endpoint.pathname)) throw new Error('Unable to add these selections. View your cart.');
    const attempt = { key: crypto.randomUUID(), items, before: TechTeesWeek.quantities(before), responseSettled: false,
      choices: this.products.map((p, i) => ({ productId: p?.id, colour: this.choices[i]?.colour, size: this.choices[i]?.size })),
      labels: indexes.map(i => `${this.products[i].day}: ${this.products[i].title}, ${this.choices[i].colour}, ${this.choices[i].size}`) };
    this.saveAttempt(attempt);
    this.announce('Adding your selections…');
    try {
      await this.read(endpoint, 'json', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items.map(item => ({ ...item, properties: { _tt_week_attempt: attempt.key } })) }) }, response => {
        attempt.responseSettled = true;
        attempt.httpStatus = response.status;
        if (TechTeesWeek.attempt?.key === attempt.key) this.saveAttempt(attempt);
      });
    } catch { /* Reconcile every response, including partial rejection or a lost body. Never replay. */ }
    if (signal.aborted) return;
    const cart = await this.cartState();
    const result = this.showCart(cart);
    if (result.complete && items.length === 1) this.dispatchEvent(new CustomEvent('tt:cart-added', { bubbles: true, detail: { items: cart.items.filter(item => item.properties?._tt_week_attempt === attempt.key) } }));
  }

  async addFive() {
    this.restoreAttempt();
    const selection = this.getSelection();
    if (this.pending || this.attempt || this.stale || !selection.ready) return;
    this.request = new AbortController();
    const signal = this.request.signal;
    this.lock(true);
    this.announce('Checking the current lineup, availability and prices…');
    try {
      await this.freshSelection(selection);
      await this.purchase(selection.items, this.products.map((_, i) => i), this.dataset.addUrl);
    } catch (error) {
      if (!signal.aborted) this.announce(this.attempt ? 'Delivery could not be confirmed. Check cart status or view your cart before adding more. Your selections are retained.' : error.message);
    } finally { if (!signal.aborted && this.isConnected) this.lock(false); }
  }

  async recoverCart() {
    if (this.pending || !this.attempt) return;
    this.request = new AbortController();
    const signal = this.request.signal;
    this.lock(true);
    this.announce('Checking your cart without adding anything…');
    try { this.showCart(await this.cartState()); }
    catch { if (!signal.aborted) this.announce('Cart status is still unavailable. View your cart before adding more.'); }
    finally { if (!signal.aborted && this.isConnected) this.lock(false); }
  }

  async submit(event) {
    const index = this.rows.findIndex(row => row.contains(event.target));
    if (index < 0 || !event.target.matches('.tt-week__form')) return;
    event.preventDefault();
    this.restoreAttempt();
    const p = this.products[index], choice = this.choices[index], form = event.target;
    if (this.pending || this.attempt) return;
    if (!choice.variant || form.querySelector('[name="id"]').value !== String(choice.variant.id)) { this.announce('Choose an available size before adding this tee.'); return; }
    this.request = new AbortController();
    const signal = this.request.signal;
    this.lock(true);
    this.announce('Checking availability…');
    try {
      const fresh = await this.read(this.endpoint(p.url, '.js'));
      const actual = fresh.variants?.find(v => String(v.id) === String(choice.variant.id));
      if (String(fresh.id) !== String(p.id) || !actual?.available || !Number.isSafeInteger(actual.price) || actual.price < 0 || actual.options?.length !== choice.variant.options.length || !actual.options?.every((v, i) => v === choice.variant.options[i])) {
        p.variants = p.variants.filter(v => String(v.id) !== String(choice.variant.id));
        this.choices[index] = TechTeesWeek.resolve(p, choice.colour, null);
        throw new Error('This size is unavailable. Choose another size.');
      }
      if (actual.price !== choice.variant.price) {
        choice.variant.price = actual.price;
        choice.variant.money = this.money(actual.price);
        throw new Error('The price changed. Review the new price before adding.');
      }
      await this.purchase([{ id: String(actual.id), quantity: 1 }], [index], form.action);
    } catch (error) {
      if (!signal.aborted) this.announce(this.attempt ? 'Delivery could not be confirmed. Check cart status or view your cart before adding more. Your selections are retained.' : error.message);
    } finally { if (!signal.aborted && this.isConnected) this.lock(false); }
  }
}
if (!customElements.get('techtees-week')) customElements.define('techtees-week', TechTeesWeek);
